import React, { FC, ReactNode, useState } from 'react';
import { Collapse } from '@mui/material';

interface Props {
    eyebrow: string;
    title: string;
    scores: (number | null | undefined)[];
    defaultOpen?: boolean;
    children: ReactNode;
}

const average = (scores: (number | null | undefined)[]) => {
    const given = scores.filter((s): s is number => typeof s === 'number');
    if (!given.length) return null;
    return Math.round((given.reduce((a, b) => a + b, 0) / given.length) * 10) / 10;
};

export const ScoreSummarySection: FC<Props> = ({
    eyebrow,
    title,
    scores,
    defaultOpen = false,
    children,
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const avg = average(scores);
    const answered = scores.filter((s) => typeof s === 'number').length;

    return (
        <div className="bg-surface border border-border-token rounded-2xl overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className="w-full flex items-center justify-between gap-3 px-[22px] py-[15px] text-left cursor-pointer bg-surface hover:bg-[#FAFAF8] dark:hover:bg-surface-2 transition-colors"
            >
                <span className="min-w-0">
                    <span className="block text-[10.5px] font-bold text-muted uppercase tracking-[0.5px]">
                        {eyebrow}
                    </span>
                    <span className="block text-[15px] font-bold text-text-1 mt-0.5">
                        {title}
                    </span>
                </span>
                <span className="flex items-center gap-3 shrink-0">
                    {avg == null ? (
                        <span className="text-[12px] text-[#B5AFA5] dark:text-muted">Not answered</span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center min-w-[42px] h-[28px] px-2 rounded-lg bg-[#C8A97E] text-white text-[13px] font-bold">
                                {`${avg} / 5`}
                            </span>
                            <span className="text-[11.5px] text-muted">
                                {`${answered} answered`}
                            </span>
                        </span>
                    )}
                    <svg
                        className="text-muted"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                            transform: open ? 'rotate(180deg)' : 'none',
                            transition: 'transform .2s',
                        }}
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </span>
            </button>
            <Collapse in={open} unmountOnExit>
                <div className="px-[22px] pb-[22px] pt-1 border-t border-border-token">
                    {children}
                </div>
            </Collapse>
        </div>
    );
};
